import { createPrefixedKey } from '@nucleus/ncu-key-prefix';
import { deepFilterNotUndefined } from '@nucleus/src-platform/data';
import {
  ContentData,
  ContentSections,
  ExtractBlockForType,
  ExtractPayload,
  SectionData,
  SectionTypeEnum,
  ThemeRichTextNodeArray,
} from '@nucleus/types/web';
import { cloneDeep as _cloneDeep, merge as _merge } from 'lodash';
import { createHyperscript } from 'slate-hyperscript';
import { basePayload } from './base-payload';

const slateHyperscript = createHyperscript({
  elements: {
    action: { type: 'action' },
    'heading-one': { type: 'heading-one' },
    'heading-two': { type: 'heading-two' },
    'heading-three': { type: 'heading-three' },
    'heading-four': { type: 'heading-four' },
    'heading-five': { type: 'heading-five' },
    'heading-six': { type: 'heading-six' },
    paragraph: { type: 'paragraph' },
    div: { type: 'div' },
    link: { type: 'link' },
    nav: { type: 'nav' },
    parameter: { type: 'parameter' },
  },
});

export interface BaseContentSections extends ContentSections {
  content: {
    main: Array<ContentData>;
  };
}

export interface BlockDescriptor<T extends SectionTypeEnum = SectionTypeEnum> {
  kind: 'block';
  props: Partial<ExtractBlockForType<T>>;
}

export interface SectionDescriptor {
  kind: 'section';
  type: SectionData['type'];
  name?: string;
  payload: Partial<SectionData['payload']>;
  addToContent: boolean;
  templateKey?: string;
}

interface PageProp {
  children?: unknown;
}

type SectionPayloadProp<T extends SectionTypeEnum> = Partial<Omit<ExtractPayload<T>, 'blocks' | 'name'>>;

type SectionProp<T extends SectionTypeEnum> = {
  type: T;
  name?: string;
  templateKey?: string;
  addToContent?: boolean;
  children?: BlockDescriptor<T> | Array<BlockDescriptor<T>>;
} & SectionPayloadProp<T>;

export function Page({ children }: PageProp): BaseContentSections {
  const descriptors = (
    Array.isArray(children) ? children.flat() : children ? [children] : []
  ) as Array<SectionDescriptor>;

  const contentSections: BaseContentSections = {
    content: { main: [] },
    sections: {},
  };

  for (const descriptor of descriptors) {
    const section = _merge(
      {},
      basePayload(descriptor.type),
      deepFilterNotUndefined({
        payload: { name: descriptor.name },
        templateKey: descriptor.templateKey,
      }),
      { payload: descriptor.payload }
    );

    if (descriptor.addToContent) {
      contentSections.content.main.push({
        id: createPrefixedKey('content'),
        type: 'section',
        payload: { sectionId: section.id },
      });
    }

    contentSections.sections[section.id] = section;
  }

  return _cloneDeep(contentSections);
}

export function Section<T extends SectionTypeEnum>({
  type,
  name,
  templateKey,
  addToContent = true,
  children,
  ...payload
}: SectionProp<T>): SectionDescriptor {
  const childArray = Array.isArray(children) ? children.flat() : children ? [children] : [];
  const blocks = childArray
    .filter((child): child is BlockDescriptor<T> => child?.kind === 'block')
    .map((block) => block.props);

  return {
    kind: 'section',
    type: type as SectionData['type'],
    name,
    payload: { ...payload, ...(blocks.length > 0 ? { blocks } : {}) } as Partial<SectionData['payload']>,
    addToContent,
    templateKey,
  };
}

type BlockProp<T extends SectionTypeEnum> = Partial<ExtractBlockForType<T>> & { children?: unknown };

export function Block<T extends SectionTypeEnum>({
  children: _children,
  ...blockProps
}: BlockProp<T>): BlockDescriptor<T> {
  return { kind: 'block', props: blockProps as Partial<ExtractBlockForType<T>> };
}

export const richText = (fragment: unknown): ThemeRichTextNodeArray => fragment as ThemeRichTextNodeArray;

export function createElement(
  tag: string | ((props: Record<string, unknown>) => unknown),
  props: Record<string, unknown> | null,
  ...children: Array<unknown>
): unknown {
  if (typeof tag === 'string') {
    return slateHyperscript(tag, props, ...children);
  }

  const flatChildren = children.flat();
  return tag({
    ...props,
    children: flatChildren.length > 0 ? flatChildren : undefined,
  });
}

export namespace createElement {
  export namespace JSX {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    export type Element = any;
    export interface IntrinsicElements {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [elementName: string]: any;
    }
    export interface ElementChildrenAttribute {
      children: {};
    }
  }
}
